import React, {useCallback, useState} from "react";
import {Stack} from "@mui/material";

import {ReportType} from "../data/reports";
import {format} from "date-fns";
import {ApiKeysClient} from "../clients/ApiKeysClient";
import {useKeycloak} from "@react-keycloak/web";
import {FormAutoCompleteItem} from "../components/report/FormAutoComplete";
import GenerateButton from "../components/report/GenerateButton";
import BasicReportParameters from "../components/report/BasicReportParameters";
import MasterReportParameters from "../components/report/MasterReportParameters.51";
import Container from "@mui/material/Container";
import {enqueueSnackbar} from "notistack";

// The backend uses capitalized snake case for the list of fields to show and aggregate by
const aggregationMapping: Record<string, string> = {
  data_type: "Data_Type",
  yop: "YOP",
  access_type: "Access_Type",
  access_method: "Access_Method",
  authors: "Authors",
  publication_date: "Publication_Date",
  article_version: "Article_Version"
};

const formDefaults = {
  data_type: [],
  yop: "",
  access_type: [],
  access_method: [],
  metric_type: [],
  granularity: "Month",
  include_parent_details: false,
  include_component_details: false,
  aggregations: {}
};

function downloadSpreadsheet(report: {fileName: string; file: Blob}) {
  if (report.file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
    const url = URL.createObjectURL(new Blob([report.file]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", report.fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode!.removeChild(link);
  }
}

export default function Reports() {
  const token = useKeycloak().keycloak.token;

  const now = new Date();
  const [form, setForm] = useState<Record<string, any>>({
    customer_id: null,
    report: null,
    platform: null,
    begin_date: new Date(now.getFullYear(), now.getMonth() - 1, 1),
    end_date: new Date(now.getFullYear(), now.getMonth(), 0),
    ...formDefaults
  });

  const [loading, setLoading] = useState(false);

  const selectedReport: ReportType = form.report && form.report.value;
  const isMaster = ["TR", "IR", "PR", "DR"].includes(selectedReport);

  // Change Callbacks
  const changeAggregation = useCallback((name: string, value: boolean) => {
    setForm(form => ({...form, aggregations: {...form.aggregations, [name]: value}}));
  }, []);

  const changeForm = useCallback((name: string, value: any) => {
    setForm(form => ({...form, [name]: value}));
  }, []);

  const changePlatform = useCallback((name: string, value: FormAutoCompleteItem | null) => {
    setForm(form => ({...form, platform: value, report: null}));
  }, []);

  const changeReport = useCallback((name: string, value: FormAutoCompleteItem | null) => {
    setForm(form => ({
      ...form,
      ...formDefaults,
      report: value
    }));
  }, []);

  const generateReport = () => {
    if (loading) return;

    if (!selectedReport) {
      return enqueueSnackbar("Report is mandatory", {variant: "warning"});
    }

    if (!form.customer_id) {
      return enqueueSnackbar("Organization is mandatory", {variant: "warning"});
    }

    if (form.begin_date > form.end_date) {
      return enqueueSnackbar("End Date needs to be after Begin Date.", {variant: "warning"});
    }

    setLoading(true);

    const getValue = (value: any) => {
      if (value instanceof Date) return format(value, "yyyy-MM-dd");
      if (Array.isArray(value)) return value.join("|");
      if (typeof value == "object") return value?.value;
      if (typeof value == "boolean") return value ? "true" : "";
      return value;
    };

    const attributes_to_show = Object.entries(form.aggregations)
      .filter(([k, v]) => v && k !== "data_type")
      .map(([k]) => aggregationMapping[k])
      .filter(v => v);

    const params = new URLSearchParams();
    Object.entries(form)
      .filter(([k, v]) => k !== "report" && k !== "aggregations")
      .concat([["attributes_to_show", attributes_to_show]])
      .map(([k, v]) => [k, getValue(v)])
      .filter(([k, v]) => !!v && v.length > 0)
      .forEach(([k, v]) => params.set(k, v));

    const client = new ApiKeysClient(token!);
    client.fetchReport("r51/" + selectedReport, params.toString())
      .then(fetchedReport => {
        downloadSpreadsheet(fetchedReport);
      }).catch(err => {
      console.error("failed to fetch report", err);
      enqueueSnackbar("Something went wrong, please check the parameters or contact the support.", {variant: "error"});
    }).finally(() => {
      setLoading(false);
    });
  };

  return (<Container sx={{backgroundColor: "white", marginTop: 5, padding: 2}} style={{maxWidth: 800}}>
    <Stack>
      <title>Reports - Thieme Counter Reporting</title>

      <BasicReportParameters
        values={form}
        changeForm={changeForm}
        changePlatform={changePlatform}
        changeReport={changeReport} />

      {isMaster && <MasterReportParameters
        values={form}
        aggregations={form.aggregations}
        changeForm={changeForm}
        changeAggregation={changeAggregation} />}

      <GenerateButton loading={loading} label={"Generate"} onClick={generateReport} />
    </Stack>
  </Container>);
}
